/**
 * Small, pure maths helpers shared by DOM, GSAP and WebGL code. No dependencies.
 * For vector or quaternion damping in R3F, use maath's `easing.damp3` / `dampQ`.
 *
 * @module
 */

/** Limits `value` to the range [`min`, `max`]. */
export function clamp(value: number, min = 0, max = 1): number {
  return Math.min(Math.max(value, min), max)
}

/** Linear interpolation: `t = 0` gives `a`, `t = 1` gives `b`. `t` is not clamped. */
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

/** Inverse of {@link lerp}: where `value` sits between `a` and `b` (0..1, unclamped). Returns 0 when `a === b`. */
export function invLerp(a: number, b: number, value: number): number {
  return a === b ? 0 : (value - a) / (b - a)
}

/**
 * Maps `value` from [`inMin`, `inMax`] to [`outMin`, `outMax`].
 * Pass `shouldClamp = true` to keep the result inside the output range.
 *
 * @example mapRange(progress, 0.2, 0.8, 0, 1, true) // a scroll window → 0..1
 */
export function mapRange(
  value: number,
  inMin: number,
  inMax: number,
  outMin: number,
  outMax: number,
  shouldClamp = false,
): number {
  const t = invLerp(inMin, inMax, value)
  return lerp(outMin, outMax, shouldClamp ? clamp(t) : t)
}

/** Hermite smoothstep with the same semantics as GLSL `smoothstep(edge0, edge1, x)`. */
export function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = clamp(invLerp(edge0, edge1, x))
  return t * t * (3 - 2 * t)
}

/**
 * Frame-rate-independent exponential smoothing toward `target`.
 *
 * @param lambda Responsiveness: higher is snappier (motion principle 5 uses 3–6).
 * @param dt Seconds since the last frame (R3F's `delta`, or GSAP ticker `deltaTime / 1000`).
 * @example ref.current = damp(ref.current, target, 4, delta)
 */
export function damp(current: number, target: number, lambda: number, dt: number): number {
  return lerp(current, target, 1 - Math.exp(-lambda * dt))
}

/** Wraps `value` into [`min`, `max`), e.g. `wrap(-1, 0, 10) === 9`. Useful for carousels and angles. */
export function wrap(value: number, min: number, max: number): number {
  const range = max - min
  if (range === 0) return min
  return min + ((((value - min) % range) + range) % range)
}

/**
 * Seeded pseudo-random generator (mulberry32). It is deterministic for a given seed and
 * returns floats in [0, 1).
 *
 * @example const random = mulberry32(hashString('Sharma Sweets')); random() // always the same
 */
export function mulberry32(seed: number): () => number {
  let state = seed >>> 0
  return () => {
    state = (state + 0x6d2b79f5) >>> 0
    let t = state
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/**
 * 32-bit FNV-1a hash of a string, as an unsigned integer. It is case-sensitive, so
 * normalise first (`trim().toLowerCase()`) if "sharma sweets" should equal "Sharma Sweets".
 */
export function hashString(value: string): number {
  let hash = 0x811c9dc5
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }
  return hash >>> 0
}
